'use client';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineMarket } from '@/app/components/icons/BaseLineMarket';
import { useToast } from '@/app/components/ui/use-toast';
import type {
  InspirationItem,
  InspirationType,
  ShortFilmType,
  ThumbScrollListProps,
  TutorialType,
  WorkWithTask,
} from '@/app/types';
import { cn, getCurrentItem, getItemType } from '@/app/utils';
import { generateCommunityShareUrl } from '@/app/utils/client';
import { Link } from '@/i18n/routing';

// 常量配置
/** 滚轮滚动触发阈值（像素）- 滚动量超过此值才触发导航 */
const WHEEL_DELTA_THRESHOLD = 3;
/** 居中判定阈值（像素）- 缩略图距离容器中心小于此值则认为已居中 */
const CENTER_THRESHOLD_PX = 50;
/** 初始加载滚动延迟（毫秒）- 首次加载时延迟滚动，确保 DOM 已完全渲染 */
const INITIAL_SCROLL_DELAY_MS = 100;
/** 媒体区域检查间隔（毫秒）- 轮询检查媒体区域是否就绪 */
const MEDIA_AREA_CHECK_INTERVAL_MS = 100;

export const ThumbScrollList: React.FC<ThumbScrollListProps> = ({
  list = [],
  onThumbnailClick,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  className,
  disabled = false,
  mediaAreaRef,
  currentId,
}) => {
  const params = useParams();
  const urlId = params.id;
  // 如果提供了 currentId，使用 currentId；否则使用 URL 中的 id
  const id = currentId || urlId;
  const t = useTranslations();
  const { toast } = useToast();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInitialScrollRef = useRef(true);
  const [isMediaAreaReady, setIsMediaAreaReady] = useState(false);
  // 维护一个内部的 currentIndex 状态，用于处理 ID 重复的情况
  const [internalCurrentIndex, setInternalCurrentIndex] = useState<number>(-1);

  // 检查媒体区域是否准备好
  useEffect(() => {
    const checkMediaArea = () => setIsMediaAreaReady(!!mediaAreaRef?.current);

    checkMediaArea();
    if (!mediaAreaRef?.current) {
      const interval = setInterval(checkMediaArea, MEDIA_AREA_CHECK_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, []);

  const getDataSource = useCallback(
    (item: InspirationType | ShortFilmType | TutorialType | WorkWithTask | InspirationItem) => {
      // 活动作品
      if ('work' in item) {
        const work = item as WorkWithTask;
        return {
          thumbUrl: work.work?.creation?.cover_uri || '',
          id: work.work?.id || '',
          isFavor: false,
          traceId: undefined,
          isActivity: true,
        };
      }
      // 灵感，短片，教程
      const communityItem = getCurrentItem(item as InspirationType | ShortFilmType | TutorialType | InspirationItem);
      const type = getItemType(communityItem);

      return {
        thumbUrl: communityItem?.creation?.cover_uri || '',
        id: communityItem?.id || '',
        isFavor: false,
        traceId: 'traceId' in item ? item.traceId : undefined,
        type,
        isActivity: false,
      };
    },
    [],
  );

  // 当 ID 或 list 变化时，更新 internalCurrentIndex
  useEffect(() => {
    if (!id) {
      setInternalCurrentIndex(-1);
      return;
    }

    // 查找匹配的索引
    const foundIndex = list.findIndex((item) => {
      const { id: itemId } = getDataSource(item);
      return itemId === id;
    });

    // 只在索引改变时更新
    setInternalCurrentIndex((prev) => {
      // 如果已经指向正确的项，不需要更新
      if (prev >= 0 && prev < list.length) {
        const currentItem = list[prev];
        if (currentItem) {
          const { id: currentItemId } = getDataSource(currentItem);
          if (currentItemId === id) {
            return prev;
          }
        }
      }
      return foundIndex;
    });
  }, [id, list, getDataSource]);

  const currentIndex = internalCurrentIndex;

  const navigateToItem = useCallback(
    (item: InspirationType | ShortFilmType | TutorialType | WorkWithTask | InspirationItem, index: number) => {
      const { id: itemId, traceId } = getDataSource(item);

      if (itemId === id) return;

      // 立即更新内部索引，避免等待 URL 变化导致的延迟
      setInternalCurrentIndex(index);
      onThumbnailClick?.(itemId, traceId || '', index);
    },
    [getDataSource, onThumbnailClick, id],
  );

  // 滚动到指定索引项，保持居中
  const scrollToItem = useCallback((index: number) => {
    if (!scrollContainerRef.current || index === undefined || index < 0) return;

    const container = scrollContainerRef.current;
    // 使用 data-thumbnail-index 来精确定位元素
    const targetThumbnail = container.querySelector(`[data-thumbnail-index="${index}"]`) as HTMLElement;

    if (!targetThumbnail) return;

    const containerRect = container.getBoundingClientRect();
    const thumbnailRect = targetThumbnail.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;
    const thumbnailCenter = thumbnailRect.top + thumbnailRect.height / 2;
    const isNearCenter = Math.abs(thumbnailCenter - containerCenter) <= CENTER_THRESHOLD_PX;

    if (!isNearCenter) {
      requestAnimationFrame(() => {
        targetThumbnail.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'nearest',
        });
      });
    }
  }, []);

  const handleNavigate = useCallback(
    (direction: 'up' | 'down') => {
      if (isLoading) return;

      const currentIdx = currentIndex ?? -1;
      let newIndex = currentIdx;

      if (direction === 'up') {
        if (currentIdx > 0) {
          newIndex = currentIdx - 1;
        } else {
          toast({ type: 'warning', message: t('creations.detail.noMoreContent') });
          return;
        }
      } else {
        if (currentIdx < list.length - 1) {
          newIndex = currentIdx + 1;
        } else {
          toast({ type: 'warning', message: t('creations.detail.noMoreContent') });
          return;
        }
      }

      if (newIndex !== currentIdx && newIndex >= 0 && newIndex < list.length) {
        const item = list[newIndex];
        if (item) {
          navigateToItem(item, newIndex);
          scrollToItem(newIndex);
        }
      }
    },
    [isLoading, currentIndex, list, toast, t, navigateToItem, scrollToItem],
  );

  // 键盘导航处理函数
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled || list.length === 0) return;

      const { key } = event;
      if (key !== 'ArrowUp' && key !== 'ArrowDown') return;

      event.preventDefault();
      event.stopPropagation();

      handleNavigate(key === 'ArrowUp' ? 'up' : 'down');
    },
    [disabled, list, handleNavigate],
  );

  // 滚轮事件处理函数
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (disabled || list.length === 0) return;

      event.preventDefault();
      event.stopPropagation();

      if (Math.abs(event.deltaY) < WHEEL_DELTA_THRESHOLD) return;
      handleNavigate(event.deltaY > 0 ? 'down' : 'up');
    },
    [disabled, list, handleNavigate],
  );

  // 添加键盘事件监听器
  useEffect(() => {
    if (disabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, disabled]);

  // 添加滚轮事件监听器（仅在媒体区域内）
  useEffect(() => {
    if (disabled || !isMediaAreaReady || !mediaAreaRef?.current) {
      return;
    }

    const mediaArea = mediaAreaRef.current;
    mediaArea.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      mediaArea.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, disabled, isMediaAreaReady, mediaAreaRef]);

  // 初始加载时滚动到当前项居中
  useEffect(() => {
    if (currentIndex !== undefined && currentIndex >= 0 && isInitialScrollRef.current) {
      const timer = setTimeout(() => {
        scrollToItem(currentIndex);
        isInitialScrollRef.current = false;
      }, INITIAL_SCROLL_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, scrollToItem]);

  if (list.length === 0 || disabled) return null;

  return (
    <nav
      className={cn('mr-8 flex h-full w-16 py-8 md:hidden', className)}
      tabIndex={!disabled ? 0 : -1}
      aria-label="缩略图导航列表"
      aria-activedescendant={currentIndex !== undefined ? `thumbnail-${currentIndex}` : undefined}
    >
      <div
        ref={scrollContainerRef}
        className="hide-scrollbar flex size-full flex-col overflow-y-auto"
        id="thumbScrollListContainer"
      >
        <InfiniteScroll
          dataLength={list.length}
          next={() => onLoadMore?.()}
          hasMore={hasMore}
          loader={null}
          scrollableTarget="thumbScrollListContainer"
          endMessage={null}
          scrollThreshold={0.8}
        >
          <div className="relative flex w-full flex-col items-end gap-2">
            {list.map((item, index) => {
              if (!item) return null;

              const { id, thumbUrl, isFavor, isActivity } = getDataSource(item);
              const link = generateCommunityShareUrl({ id, withOrigin: false, isActivity });

              return (
                <Link
                  prefetch={false}
                  href={link}
                  key={`${index}-${id}`}
                  id={`thumbnail-${index}`}
                  data-thumbnail-index={index}
                  data-thumbnail-id={id}
                  className={cn(
                    'relative size-14 cursor-pointer overflow-hidden rounded-8 border-0 bg-transparent p-0 transition-all duration-200',
                    currentIndex === index && 'size-16 border-[2px] border-system-white p-[2px]',
                  )}
                  onClick={(e) => {
                    e.preventDefault();

                    if (currentIndex === index || isLoading) return;
                    navigateToItem(item, index);
                  }}
                  aria-selected={currentIndex === index}
                  tabIndex={-1}
                >
                  {thumbUrl ? (
                    <img src={thumbUrl} alt="" className="h-full w-full rounded-6 bg-system-black10 object-cover" />
                  ) : (
                    <div className="h-full w-full rounded-6 bg-system-black10" />
                  )}
                  {isFavor && (
                    <span className="absolute top-0 right-0 flex size-5 items-center justify-center rounded-tr-8 rounded-bl-8 bg-black/50">
                      <BaseLineMarket className="text-[#FFCC00] text-sm" />
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
    </nav>
  );
};
